import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { useEffect, useRef, useState } from "react";
import type { StreamEvent } from "../../shared/streamEvents";
import { chatStreamPath } from "../lib/apiPaths";

export type StreamMessage = {
  role: "assistant" | "reasoning" | "tool";
  text: string;
  toolInput?: ReadonlyJSONValue;
  status: "streaming" | "done";
};

type RunStatus = "idle" | "running";

type StreamingState = {
  messages: ReadonlyMap<string, StreamMessage>;
  runStatus: RunStatus;
};

const FLUSH_INTERVAL_MS = 33;
const RECONNECT_DELAY_MS = 500;

const EMPTY_MESSAGES: ReadonlyMap<string, StreamMessage> = new Map();
const IDLE_STATE: StreamingState = {
  messages: EMPTY_MESSAGES,
  runStatus: "idle",
};

/**
 * Streams a run's SSE events into per-message text, flushed every ~33ms.
 * `isRunLive` must be the replicated run-lease heartbeat, not optimistic
 * state: the endpoint 404s until the chat commits, and connecting replays
 * the run so far, so opening late loses nothing.
 */
export function useStreamingText(
  chatId: string | null,
  isRunLive: boolean,
  onRunFinished?: (messages: ReadonlyMap<string, StreamMessage>) => void
): StreamingState {
  const [state, setState] = useState<StreamingState>(IDLE_STATE);
  const accRef = useRef<Map<string, StreamMessage>>(new Map());
  const dirtyRef = useRef(false);
  const runStatusRef = useRef<RunStatus>("idle");
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onRunFinishedRef = useRef(onRunFinished);
  useEffect(() => {
    onRunFinishedRef.current = onRunFinished;
  });

  useEffect(() => {
    if (!isRunLive || !chatId) return;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setState({
          messages: new Map(accRef.current),
          runStatus: runStatusRef.current,
        });
      }
    };

    const flushImmediate = () => {
      dirtyRef.current = false;
      setState({
        messages: new Map(accRef.current),
        runStatus: runStatusRef.current,
      });
    };

    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);

    const connect = () => {
      const url = chatStreamPath(chatId);
      source = new EventSource(url, { withCredentials: true });

      source.onerror = () => {
        // The browser never retries a non-2xx close (e.g. a deploy 502).
        if (source?.readyState === EventSource.CLOSED) {
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      source.onmessage = (msg) => {
        let event: StreamEvent;
        try {
          event = JSON.parse(msg.data);
        } catch {
          return;
        }
        const acc = accRef.current;

        switch (event.type) {
          case "run-started":
            runStatusRef.current = "running";
            flushImmediate();
            return;
          case "message-start":
            acc.set(event.messageId, {
              role: event.role,
              text: "",
              status: "streaming",
            });
            flushImmediate();
            return;
          case "message-content": {
            const existing = acc.get(event.messageId);
            if (existing) {
              acc.set(event.messageId, {
                ...existing,
                text: existing.text + event.delta,
              });
            }
            dirtyRef.current = true;
            return;
          }
          case "tool-call-preview": {
            const existing = acc.get(event.messageId);
            if (existing) {
              acc.set(event.messageId, {
                ...existing,
                toolInput: event.input,
              });
            }
            dirtyRef.current = true;
            return;
          }
          case "message-end": {
            const existing = acc.get(event.messageId);
            if (existing) {
              acc.set(event.messageId, { ...existing, status: "done" });
            }
            flushImmediate();
            return;
          }
          case "run-finished":
            runStatusRef.current = "idle";
            for (const [messageId, entry] of acc) {
              acc.set(messageId, { ...entry, status: "done" });
            }
            flushImmediate();
            onRunFinishedRef.current?.(acc);
            return;
        }
      };
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      source?.close();
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
      accRef.current = new Map();
      runStatusRef.current = "idle";
      setState(IDLE_STATE);
    };
  }, [chatId, isRunLive]);

  return state;
}
